import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Phone, Mail, Facebook, Instagram, Linkedin } from "lucide-react";
import { CONTACT, APPS } from "@/lib/content";
import { Logo } from "@/components/Brand";

const QUICK_LINKS = [
  { label: "Home", to: "/" },
  { label: "Services", to: "/#services" },
  { label: "Business Advisory", to: "/business-advisory" },
  { label: "About", to: "/#about" },
  { label: "Client Centre", to: "/client-centre" },
  { label: "Tax Insights", to: "/insights" },
  { label: "Contact", to: "/#contact" },
];

const CRA_MY_ACCOUNT = "https://www.canada.ca/en/revenue-agency/services/e-services/cra-login-services.html";

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#102036] text-[#FFFFFF]" data-testid="site-footer">
      <div className="mx-auto max-w-[1320px] px-5 py-16 sm:px-8 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr]">
          <div>
            <Logo light />
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-[#FFFFFF]/70">
              Professional accounting, taxation and business advisory for individuals and businesses — established in Newmarket, Ontario.
            </p>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#FFFFFF]/50">Quick Links</p>
            <ul className="mt-5 space-y-3 text-sm">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(" ", "-")}`} className="text-[#FFFFFF]/80 transition-colors hover:text-[#FFFFFF]">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#FFFFFF]/50">Client Applications</p>
            <ul className="mt-5 space-y-3 text-sm">
              {[APPS.clientManager, APPS.taxManager].map((a) => (
                <li key={a.name}>
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-app-${a.name.toLowerCase().replace(" ", "-")}`}
                    className="inline-flex items-center gap-1.5 text-[#FFFFFF]/80 transition-colors hover:text-[#FFFFFF]"
                  >
                    {a.name} <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={CRA_MY_ACCOUNT}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-app-cra-my-account"
                  className="inline-flex items-center gap-1.5 text-[#FFFFFF]/80 transition-colors hover:text-[#FFFFFF]"
                >
                  CRA My Account <ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#FFFFFF]/50">Contact</p>
            <ul className="mt-5 space-y-3 text-sm text-[#FFFFFF]/80">
              <li className="flex gap-2.5">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#3682AE]" />
                <span>{CONTACT.addressLines.join(", ")}</span>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="footer-phone-link" className="flex gap-2.5 transition-colors hover:text-[#FFFFFF]">
                  <Phone className="mt-0.5 h-4 w-4 shrink-0 text-[#3682AE]" />
                  {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email-link" className="flex gap-2.5 transition-colors hover:text-[#FFFFFF]">
                  <Mail className="mt-0.5 h-4 w-4 shrink-0 text-[#3682AE]" />
                  {CONTACT.email}
                </a>
              </li>
            </ul>
            <div className="mt-6 flex gap-3">
              {[
                { icon: Facebook, label: "Facebook", url: "https://www.facebook.com/thefordgroup" },
                { icon: Instagram, label: "Instagram", url: "https://www.instagram.com/ford_associates/" },
                { icon: Linkedin, label: "LinkedIn", url: "https://www.linkedin.com/company/fordassociatesca/" },
              ].map(({ icon: Icon, label, url }) => (
                <a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Ford & Associates on ${label} (opens in a new tab)`}
                  data-testid={`footer-social-${label.toLowerCase()}`}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-[#FFFFFF]/20 text-[#FFFFFF]/70 transition-colors hover:border-[#3682AE] hover:text-[#FFFFFF]"
                >
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-4 border-t border-[#FFFFFF]/10 pt-8 text-xs text-[#FFFFFF]/50 sm:flex-row sm:items-center sm:justify-between">
          <p data-testid="footer-copyright">© {year} Ford &amp; Associates. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="https://postings.cloud"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-postings-button"
              className="inline-flex items-center gap-1.5 rounded-full border border-[#FFFFFF]/25 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.14em] text-[#FFFFFF]/80 transition-colors hover:border-[#3682AE] hover:text-[#FFFFFF]"
            >
              Postings Inc. <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors hover:text-[#FFFFFF]">Privacy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="transition-colors hover:text-[#FFFFFF]">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
